/**
 * Keyboard navigation between the events of a calendar grid (WAI-ARIA roving tab stop): one event is in the tab
 * order (the selected one, else the first); arrow keys move to the previous/next event in time, Home/End to the
 * first/last. Alt+arrows stay with moving an event (time grid).
 */
import type { KeyboardEvent } from 'react';
import type { CalendarGridEvent } from './model';

/** Events in reading order (start, then all-day first). */
export const inTimeOrder = (events: readonly CalendarGridEvent[]): CalendarGridEvent[] =>
  [...events].sort((a, b) => a.start.getTime() - b.start.getTime() || Number(b.allDay) - Number(a.allDay) || a.id.localeCompare(b.id));

/** The id that has the tab stop. */
export const tabStopOf = (ordered: readonly CalendarGridEvent[], selectedId: string | undefined): string | undefined =>
  (selectedId && ordered.some((e) => e.id === selectedId) ? selectedId : ordered[0]?.id);

const KEYS: Record<string, number> = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };

/** onKeyDown of the grid: moves the focus between event chips. */
export function rovingKeyDown(ordered: readonly CalendarGridEvent[]) {
  return (event: KeyboardEvent<HTMLElement>) => {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    const chip = (event.target as Element).closest<HTMLElement>('[data-event-chip]');
    if (!chip) return;
    const id = chip.getAttribute('data-context-id');
    const index = ordered.findIndex((e) => e.id === id);
    if (index < 0) return;
    const step = KEYS[event.key];
    const target = event.key === 'Home' ? 0 : event.key === 'End' ? ordered.length - 1 : step !== undefined ? index + step : undefined;
    if (target === undefined) return;
    const next = ordered[Math.max(0, Math.min(ordered.length - 1, target))];
    if (!next) return;
    event.preventDefault();
    const root = event.currentTarget;
    for (const element of root.querySelectorAll<HTMLElement>('[data-event-chip]')) {
      if (element.getAttribute('data-context-id') === next.id) {
        element.focus();
        break;
      }
    }
  };
}
